<section class="ei-table" [class.ei-table--loading]="loading">
    @if (!hideToolbar) {
        <div class="ei-table__toolbar" [class.ei-table__toolbar--selection]="hasSelection">
            @if (hasSelection) {
                <ng-content select="[toolbarSelection]"></ng-content>
            } @else {
                <div class="ei-table__toolbar-start">
                    <div class="ei-table__toolbar-left">
                        @if (toolbarSearchTemplate) {
                            <ng-container
                                *ngTemplateOutlet="
                                    toolbarSearchTemplate;
                                    context: {
                                        value: globalSearch,
                                        valueChange: onGlobalSearchChange,
                                    }
                                "
                            ></ng-container>
                        } @else {
                            <ei-search-bar
                                class="ei-table__global-search"
                                [placeholder]="
                                    globalSearchPlaceholder ?? text.globalSearchPlaceholder
                                "
                                [ngModel]="globalSearch"
                                (valueChange)="onGlobalSearchChange($event)"
                            ></ei-search-bar>
                        }
                    </div>

                    <div class="ei-table__toolbar-extra">
                        <ng-content select="[toolbarExtra]"></ng-content>
                    </div>
                </div>

                <div class="ei-table__toolbar-right">
                    <span class="ei-table__rows-info"
                        >{{ totalItems }} {{ recordsFoundSuffix ?? text.recordsFound }}</span
                    >
                    <ei-dropdown
                        class="ei-table__manage-columns"
                        [config]="manageColumnsConfig"
                        [ngModel]="selectedVisibleColumnsModel"
                        (ngModelChange)="onVisibleColumnsChange($event)"
                    ></ei-dropdown>
                    @if (rightSideActionBtns) {
                        <ng-container
                            *ngTemplateOutlet="
                                rightSideActionBtns;
                                context: {
                                    value: globalSearch,
                                    valueChange: onGlobalSearchChange,
                                }
                            "
                        ></ng-container>
                    }
                </div>
            }
        </div>
    }

    <div class="ei-table__wrap">
        <table class="ei-table__grid" [attr.aria-busy]="loading">
            <thead>
                <tr class="ei-table__tr ei-table__tr--head">
                    @if (showCheckboxColumn) {
                        <th
                            class="ei-table__th ei-table__th--checkbox"
                            [style.width.px]="CHECKBOX_COLUMN_WIDTH"
                            (click)="$event.stopPropagation()"
                        >
                            <div class="ei-table__th-content">
                                <input
                                    type="checkbox"
                                    class="ei-table__checkbox"
                                    [checked]="isAllRowsSelected()"
                                    [indeterminate]="isSomeRowsSelected() && !isAllRowsSelected()"
                                    (change)="toggleAllRowsSelection()"
                                    [attr.aria-label]="text.selectAllRowsAriaLabel"
                                />
                            </div>
                        </th>
                    }

                    <!-- Row number column header -->
                    @if (showRowNumberColumn) {
                        <th
                            class="ei-table__th ei-table__th--row-number"
                            [style.width.px]="ROW_NUMBER_COLUMN_WIDTH"
                        >
                            <div class="ei-table__th-content">
                                <span class="ei-table__th-text">#</span>
                            </div>
                        </th>
                    }
                    @for (col of visibleColumns; track col.key) {
                        <th
                            class="ei-table__th"
                            [class.ei-table__th--sortable]="col.sortable"
                            [class.ei-table__th--editable]="col.editable"
                            [style.width.px]="col.widthPx ?? null"
                            [attr.data-col-key]="col.key"
                            (click)="onHeaderSort(col)"
                        >
                            <div class="ei-table__th-content">
                                <span class="ei-table__th-text-wrapper">
                                    <span class="ei-table__th-text">{{ col.header }}</span>
                                    @if (col.sortable) {
                                        <span class="ei-table__sort-icon"></span>
                                    }
                                </span>
                                @if (getFilterDropdownConfig(col); as cfg) {
                                    <span
                                        class="ei-table__th-actions"
                                        (click)="$event.stopPropagation()"
                                    >
                                        <ei-dropdown
                                            class="ei-table__header-filter"
                                            [config]="cfg"
                                            [triggerMode]="DropdownTriggerMode.Icon"
                                            [triggerAriaLabel]="
                                                text.filterAriaLabelPrefix + col.header
                                            "
                                            [overlayMinWidthPx]="240"
                                            [ngModel]="selectFilters[col.key] ?? null"
                                            (ngModelChange)="onFilterChange(col.key, $event)"
                                        ></ei-dropdown>
                                    </span>
                                }
                            </div>
                        </th>
                    }
                </tr>
            </thead>

            <tbody>
                @if (loading) {
                    @for (skeletonIdx of skeletonRowIndices; track skeletonIdx) {
                        <tr class="ei-table__tr ei-table__tr--skeleton" aria-hidden="true">
                            @if (showCheckboxColumn) {
                                <td
                                    class="ei-table__td ei-table__td--checkbox"
                                    (click)="$event.stopPropagation()"
                                >
                                    <div class="ei-table__cell ei-table__cell--skeleton">
                                        <div
                                            class="ei-table__skeleton-block ei-table__skeleton-block--checkbox"
                                        ></div>
                                    </div>
                                </td>
                            }
                            @if (showRowNumberColumn) {
                                <td
                                    class="ei-table__td ei-table__td--row-number"
                                    [style.width.px]="ROW_NUMBER_COLUMN_WIDTH"
                                >
                                    <div class="ei-table__cell ei-table__cell--skeleton">
                                        <div
                                            class="ei-table__skeleton-block ei-table__skeleton-block--narrow"
                                        ></div>
                                    </div>
                                </td>
                            }
                            @for (col of visibleColumns; track col.key) {
                                <td
                                    class="ei-table__td"
                                    [style.width.px]="col.widthPx ?? null"
                                    [attr.data-col-key]="col.key"
                                >
                                    <div class="ei-table__cell ei-table__cell--skeleton">
                                        <div class="ei-table__skeleton-block"></div>
                                    </div>
                                </td>
                            }
                        </tr>
                    }
                } @else if (!pagedRows.length) {
                    <tr class="ei-table__tr ei-table__tr--empty">
                        <td
                            class="ei-table__td ei-table__td--empty"
                            [attr.colspan]="getEmptyColspan()"
                        >
                            {{
                                !rows.length && emptyTableMessage
                                    ? emptyTableMessage
                                    : rows.length
                                      ? text.noResults
                                      : text.noData
                            }}
                        </td>
                    </tr>
                } @else {
                    @for (row of pagedRows; track getRowId(row); let rowIndex = $index) {
                        <tr
                            class="ei-table__tr ei-table__tr--body"
                            [ngClass]="rowClassGetter ? rowClassGetter(row) : null"
                            [class.ei-table__tr--clickable]="isRowClickEnabled"
                            [class.ei-table__tr--selected]="
                                selectedRowId != null && getRowId(row) === selectedRowId
                            "
                            (click)="onRowClick(row, $event)"
                        >
                            @if (showCheckboxColumn) {
                                <td
                                    class="ei-table__td ei-table__td--checkbox"
                                    (click)="$event.stopPropagation()"
                                >
                                    <div class="ei-table__cell">
                                        <input
                                            type="checkbox"
                                            class="ei-table__checkbox"
                                            [checked]="isRowSelected(row)"
                                            (change)="toggleRowSelection(row)"
                                            [attr.aria-label]="text.selectRowAriaLabel"
                                        />
                                    </div>
                                </td>
                            }

                            <!-- Row number column cell -->
                            @if (showRowNumberColumn) {
                                <td
                                    class="ei-table__td ei-table__td--row-number"
                                    [style.width.px]="ROW_NUMBER_COLUMN_WIDTH"
                                >
                                    <div class="ei-table__cell">
                                        <span class="ei-table__cell-text">{{
                                            getRowNumber(rowIndex)
                                        }}</span>
                                    </div>
                                </td>
                            }
                            @for (col of visibleColumns; track col.key) {
                                <td
                                    class="ei-table__td"
                                    [class.ei-table__td--editable-full-hit]="
                                        editableCellFullTdClick &&
                                        col.editable &&
                                        !isActionsColumn(col) &&
                                        !isDownloadColumn(col) &&
                                        !col.cellTemplate
                                    "
                                    [style.width.px]="col.widthPx ?? null"
                                    [attr.data-col-key]="col.key"
                                    (click)="onEditableTdClick($event, row, col)"
                                >
                                    @if (isActionsColumn(col)) {
                                        <div class="ei-table__actions">
                                            @for (a of actions; track a.id) {
                                                @if (!isActionHidden(a, row)) {
                                                    @if (
                                                        getMoreAction()?.id === a.id &&
                                                        rowActionsPanelTemplate
                                                    ) {
                                                        <ei-dropdown
                                                            class="ei-table__row-actions-dropdown"
                                                            [config]="rowActionsDropdownConfig"
                                                            [overlayMinWidthPx]="200"
                                                            [openAbove]="true"
                                                        >
                                                            <ng-template
                                                                #dropdownTrigger
                                                                let-toggle="toggle"
                                                            >
                                                                <ei-button
                                                                    variant="tertiary"
                                                                    size="sm"
                                                                    iconPosition="only"
                                                                    [icon]="a.icon"
                                                                    [ariaLabel]="a.ariaLabel"
                                                                    type="button"
                                                                    (buttonClick)="toggle()"
                                                                ></ei-button>
                                                            </ng-template>
                                                            <ng-template
                                                                #dropdownPanel
                                                                let-close="close"
                                                            >
                                                                <ng-container
                                                                    *ngTemplateOutlet="
                                                                        rowActionsPanelTemplate;
                                                                        context: {
                                                                            row: row,
                                                                            close: close,
                                                                        }
                                                                    "
                                                                ></ng-container>
                                                            </ng-template>
                                                        </ei-dropdown>
                                                    } @else if (a.label) {
                                                        <ei-button
                                                            [variant]="a.variant ?? 'primary'"
                                                            size="sm"
                                                            [label]="a.label"
                                                            [ariaLabel]="a.ariaLabel"
                                                            [disabled]="isActionDisabled(a, row)"
                                                            type="button"
                                                            (buttonClick)="onAction(a.id, row)"
                                                        ></ei-button>
                                                    } @else {
                                                        <ei-button
                                                            variant="tertiary"
                                                            size="sm"
                                                            iconPosition="only"
                                                            [icon]="a.icon"
                                                            [ariaLabel]="a.ariaLabel"
                                                            [disabled]="isActionDisabled(a, row)"
                                                            (buttonClick)="onAction(a.id, row)"
                                                        ></ei-button>
                                                    }
                                                }
                                            }
                                        </div>
                                    } @else if (isEditingCell(row, col)) {
                                        <div class="ei-table__edit">
                                            <div class="ei-table__edit-main">
                                                <input
                                                    class="ei-table__edit-input"
                                                    [formControl]="editControl"
                                                    (keydown)="onEditKeydown($event, row, col)"
                                                    (blur)="onEditBlur(row, col)"
                                                />
                                                @if (inlineEditActions !== 'none') {
                                                    <div class="ei-table__edit-actions">
                                                        <button
                                                            type="button"
                                                            class="ei-table__edit-action-btn ei-table__edit-action-btn--save"
                                                            [attr.aria-label]="text.saveAriaLabel"
                                                            [disabled]="!!editInlineError"
                                                            (click)="commitEdit(row, col)"
                                                        >
                                                            @if (inlineEditActions === 'text') {
                                                                <span
                                                                    class="ei-table__edit-action-text"
                                                                    >Save</span
                                                                >
                                                            } @else {
                                                                <span
                                                                    class="ei-table__edit-action-icon"
                                                                    ><app-svg-icon
                                                                        name="check"
                                                                        size="lg"
                                                                /></span>
                                                            }
                                                        </button>
                                                        <button
                                                            type="button"
                                                            class="ei-table__edit-action-btn ei-table__edit-action-btn--cancel"
                                                            [attr.aria-label]="text.cancelAriaLabel"
                                                            (click)="cancelEdit()"
                                                        >
                                                            @if (inlineEditActions === 'text') {
                                                                <span
                                                                    class="ei-table__edit-action-text"
                                                                    >Cancel</span
                                                                >
                                                            } @else {
                                                                <span
                                                                    class="ei-table__edit-action-icon"
                                                                    ><app-svg-icon
                                                                        name="close"
                                                                        size="lg"
                                                                /></span>
                                                            }
                                                        </button>
                                                    </div>
                                                }
                                            </div>
                                            @if (editInlineError) {
                                                <span class="ei-table__edit-error">{{
                                                    editInlineError
                                                }}</span>
                                            }
                                        </div>
                                    } @else if (isDownloadColumn(col)) {
                                        <div class="ei-table__cell ei-table__actions">
                                            <ei-button
                                                variant="primary"
                                                size="sm"
                                                [label]="col.downloadLabel ?? 'Download'"
                                                [ariaLabel]="col.downloadLabel ?? 'Download'"
                                                type="button"
                                                [disabled]="col.disable ? col.disable(row) : false"
                                                (buttonClick)="onDownloadClick(row)"
                                            ></ei-button>
                                        </div>
                                    } @else if (col.cellTemplate === 'statusBadge') {
                                        <div class="ei-table__cell">
                                            <span
                                                class="ei-table__status-badge"
                                                [class]="
                                                    'ei-table__status-badge--' +
                                                    getClassSafeText(cellText(row, col))
                                                "
                                                [title]="cellText(row, col)"
                                                >{{ cellText(row, col) }}</span
                                            >
                                        </div>
                                    } @else if (col.cellTemplate === 'image') {
                                        @let imageSrc = cellText(row, col);
                                        <div class="ei-table__cell ei-table__cell--image">
                                            @if (imageSrc) {
                                                <img
                                                    class="ei-table__thumb"
                                                    [src]="imageSrc"
                                                    [alt]="cellTitle(row, col)"
                                                    loading="lazy"
                                                />
                                            } @else {
                                                <span
                                                    class="ei-table__thumb ei-table__thumb--empty"
                                                    aria-hidden="true"
                                                ></span>
                                            }
                                        </div>
                                    } @else if (col.cellTemplate === 'expiryDate') {
                                        @let expiryText = cellText(row, col);
                                        @let expiryDateText = getExpiryDateText(expiryText);
                                        @let expiryWarningText = getExpiryWarningText(expiryText);
                                        <div class="ei-table__cell ei-table__cell--expiry">
                                            <span class="ei-table__expiry-date">{{
                                                expiryDateText
                                            }}</span>
                                            @if (expiryWarningText) {
                                                <span class="ei-table__expiry-warning">{{
                                                    '(' + expiryWarningText + ')'
                                                }}</span>
                                            }
                                        </div>
                                    } @else {
                                        @let hintTitle = cellHintTitle(row, col);
                                        @let clickable = isCellClickable(col, row);
                                        <div
                                            class="ei-table__cell"
                                            [class.ei-table__cell--with-hint]="hintTitle"
                                        >
                                            @if (clickable) {
                                                <button
                                                    type="button"
                                                    class="ei-table__cell-link"
                                                    [ngClass]="cellClass(row, col)"
                                                    [title]="cellTitle(row, col)"
                                                    (click)="onCellClick($event, row, col)"
                                                >
                                                    {{ cellText(row, col) }}
                                                </button>
                                            } @else {
                                                <span
                                                    class="ei-table__cell-text"
                                                    [class.ei-table__cell-text--editable]="
                                                        col.editable
                                                    "
                                                    [class.ei-table__cell-text--placeholder]="
                                                        col.editable && !cellText(row, col)
                                                    "
                                                    [ngClass]="cellClass(row, col)"
                                                    [title]="cellTitle(row, col)"
                                                    (click)="
                                                        !editableCellFullTdClick &&
                                                            col.editable &&
                                                            !isActionsColumn(col) &&
                                                            startEdit(row, col)
                                                    "
                                                    >{{
                                                        cellText(row, col) ||
                                                            (col.editable
                                                                ? col.emptyPlaceholder || 'Add'
                                                                : '')
                                                    }}</span
                                                >
                                            }
                                            @if (hintTitle) {
                                                <span
                                                    class="ei-table__cell-hint"
                                                    role="img"
                                                    [attr.aria-label]="hintTitle"
                                                    [attr.title]="hintTitle"
                                                    data-testid="ei-table-cell-hint"
                                                    (click)="$event.stopPropagation()"
                                                >
                                                    <app-svg-icon
                                                        [name]="col.cellHintIcon ?? 'info'"
                                                        [size]="col.cellHintIconSize ?? 'xs'"
                                                    />
                                                </span>
                                            }
                                        </div>
                                    }
                                </td>
                            }
                        </tr>
                    }
                }
            </tbody>
        </table>
    </div>

    @if (!hideFooter) {
        <div class="ei-table__footer-wrapper">
            <div class="ei-table__footer">
                <div class="ei-table__page-size">
                    <ei-dropdown
                        class="ei-table__page-size-dropdown"
                        [config]="pageSizeConfig"
                        [ngModel]="pageSize"
                        [openAbove]="true"
                        (ngModelChange)="onPageSizeChange($event)"
                    ></ei-dropdown>
                    <span class="ei-table__page-size-suffix">{{ text.pageSizeSuffix }}</span>
                </div>

                <div class="ei-table__pager">
                    <div class="ei-table__page-info">
                        @if (useRangePaginationFormat) {
                            {{ text.showing }}
                            {{ getStartRowNumber() }}-{{ getEndRowNumber() }}/{{ totalItems }}
                            {{ footerEntriesLabel ?? text.items }}
                        } @else {
                            {{ text.showing }}
                            {{ getStartRowNumber() }}
                            {{ text.to }}
                            {{ getEndRowNumber() }}
                            {{ text.of }}
                            {{ totalItems }}
                            {{ footerEntriesLabel ?? text.entries }}
                        }
                    </div>

                    <div class="ei-table__pager-controls">
                        <button
                            type="button"
                            class="ei-table__nav-btn"
                            [disabled]="loading || isFirstPage"
                            (click)="onPrev()"
                        >
                            <span class="ei-table__nav-icon"
                                ><app-svg-icon name="chevron_left" size="lg"
                            /></span>
                            <span>{{ text.prev }}</span>
                        </button>

                        <div class="ei-table__pages">
                            @for (page of visiblePages; track page) {
                                @if (isEllipsis(page)) {
                                    <span class="ei-table__ellipsis">...</span>
                                } @else {
                                    <button
                                        type="button"
                                        class="ei-table__page-btn"
                                        [class.ei-table__page-btn--active]="page === currentPage"
                                        [disabled]="loading"
                                        (click)="onGoTo(page)"
                                    >
                                        {{ page }}
                                    </button>
                                }
                            }
                        </div>

                        <button
                            type="button"
                            class="ei-table__nav-btn"
                            [disabled]="loading || isLastPage"
                            (click)="onNext()"
                        >
                            <span>{{ text.next }}</span>
                            <span class="ei-table__nav-icon"
                                ><app-svg-icon name="chevron_right" size="lg"
                            /></span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    }
</section>
